import { useState } from 'react';
import { useCurrentStreak, useStreakShields } from '@busy/api';
import { useT } from '../../lib/i18n';

// Gold ribbon with flame icon + day count. Mirrors the demo's .streak-ribbon
// component. Surfaces both protection layers as stacked chips on the right:
// snowflake (cooloff bank, +1 per 3 alive days, max 3) on top, shield (max 6,
// 1 per 100 alive days + anniversary refills) on bottom. Both chips are
// tappable — tap reveals an info row underneath the ribbon showing the
// detailed status + days until the next earn.
//
// Rescue prompt for a broken streak lives in <RescueStreakModal /> mounted
// at the app root — fires when broke_at is set on the streak row.
//
// Optional `onClick` makes the ribbon a button (Today wires it to scroll
// to today's lesson — opening a lesson or logging a cut is the only way
// to extend the streak, so a tap-to-jump shortcut is the obvious
// affordance). Chips stop propagation so tapping them doesn't also
// trigger the lesson scroll.

const COOLOFF_MAX = 3;
const COOLOFF_INTERVAL = 3; // alive days per cooloff earned
const SHIELD_MAX = 6;
const SHIELD_INTERVAL = 100; // alive days per shield earned

type ChipKind = 'cooloff' | 'shield';

export function StreakRibbon({ onClick }: { onClick?: () => void } = {}) {
  const streak = useCurrentStreak();
  const shields = useStreakShields();
  const t = useT();
  const [expanded, setExpanded] = useState<ChipKind | null>(null);

  if (streak.loading) {
    return <div className="h-20 rounded-2xl bg-bg-alt animate-pulse" />;
  }

  const days = streak.data?.current_streak ?? 0;
  const aliveDays = streak.data?.alive_day_count ?? 0;
  const cooloffs = streak.data?.cooloffs_available ?? 0;
  const shieldCount = shields.data?.length ?? 0;

  const subtitle =
    days === 0 ? t('today.streak.start') : days === 1 ? t('today.streak.day1') : t('today.streak.dayN');

  // Days until next cooloff/shield based on alive_day_count progression.
  // At cap → null (no countdown). Otherwise = interval - (alive % interval),
  // which gives 3..1 for the cooloff cycle and 100..1 for shields.
  const cooloffAtCap = cooloffs >= COOLOFF_MAX;
  const cooloffNextIn = cooloffAtCap
    ? null
    : COOLOFF_INTERVAL - (aliveDays % COOLOFF_INTERVAL) || COOLOFF_INTERVAL;
  const shieldAtCap = shieldCount >= SHIELD_MAX;
  const shieldNextIn = shieldAtCap
    ? null
    : SHIELD_INTERVAL - (aliveDays % SHIELD_INTERVAL) || SHIELD_INTERVAL;

  function toggleChip(kind: ChipKind, e: React.MouseEvent) {
    // Stop the click from bubbling into the ribbon's lesson-jump handler.
    e.stopPropagation();
    setExpanded((prev) => (prev === kind ? null : kind));
  }

  const ribbonInner = (
    <>
      <FlameIcon className="w-9 h-9 text-red-600 shrink-0" />
      <div className="flex-1 min-w-0 text-left">
        <div className="font-display text-3xl leading-none tabular-nums">{days}</div>
        <div className="text-[11px] font-bold tracking-[0.08em] uppercase mt-1">{subtitle}</div>
      </div>
      {/* Chips stacked vertically — snowflake cooloff above, shield below. */}
      <div className="flex flex-col gap-1 shrink-0">
        <ChipButton
          icon={<SnowflakeIcon className="w-3 h-3" />}
          count={cooloffs}
          dim={cooloffs === 0}
          active={expanded === 'cooloff'}
          ariaLabel={t('today.streak.cooloffTitle')}
          onClick={(e) => toggleChip('cooloff', e)}
        />
        <ChipButton
          icon={<ShieldIcon className="w-3 h-3" />}
          count={shieldCount}
          dim={shieldCount === 0}
          active={expanded === 'shield'}
          ariaLabel={t('today.streak.shieldTitle')}
          onClick={(e) => toggleChip('shield', e)}
        />
      </div>
    </>
  );

  // The outer container can't be a <button> because we have inner <button>
  // chips (nested buttons are invalid HTML). When `onClick` is provided we
  // add role="button" + tabIndex + keyboard handler so the whole ribbon
  // remains keyboard-accessible.
  const onKey = onClick
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }
    : undefined;

  return (
    <div className="space-y-2">
      <div
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-label={onClick ? t('today.streak.openLessonAria') : undefined}
        onClick={onClick}
        onKeyDown={onKey}
        className={`w-full rounded-2xl bg-gradient-to-br from-gold-500 to-gold-400 text-navy-900 p-4 flex items-center gap-3 shadow-md ${
          onClick ? 'cursor-pointer hover:shadow-lg active:scale-[0.99] transition-all' : ''
        }`}
      >
        {ribbonInner}
      </div>

      {/* Expanded info row — shows under the ribbon when a chip is tapped.
          Mirrors the chip's content (cooloff vs shield) with a clear
          "next earned in N days" line. */}
      {expanded === 'cooloff' && (
        <div className="rounded-xl border border-line bg-bg-alt p-3 text-[12px] text-ink-700 leading-snug space-y-1.5">
          <div className="flex items-center gap-2">
            <SnowflakeIcon className="w-4 h-4 text-navy-900 dark:text-gold-500" />
            <strong>{t('today.streak.cooloff.title')}</strong>
            <span className="ml-auto tabular-nums text-ink-500">
              {t('today.streak.cooloff.banked', { count: cooloffs, max: COOLOFF_MAX })}
            </span>
          </div>
          <p>{t('today.streak.cooloff.body')}</p>
          <p className="font-bold">
            {cooloffNextIn == null
              ? t('today.streak.cooloff.atCap')
              : t('today.streak.cooloff.nextIn', { days: cooloffNextIn })}
          </p>
        </div>
      )}
      {expanded === 'shield' && (
        <div className="rounded-xl border border-line bg-bg-alt p-3 text-[12px] text-ink-700 leading-snug space-y-1.5">
          <div className="flex items-center gap-2">
            <ShieldIcon className="w-4 h-4 text-navy-900 dark:text-gold-500" />
            <strong>{t('today.streak.shield.title')}</strong>
            <span className="ml-auto tabular-nums text-ink-500">
              {t('today.streak.shield.banked', { count: shieldCount, max: SHIELD_MAX })}
            </span>
          </div>
          <p>{t('today.streak.shield.body')}</p>
          <p className="font-bold">
            {shieldNextIn == null
              ? t('today.streak.shield.atCap')
              : t('today.streak.shield.nextIn', { days: shieldNextIn })}
          </p>
        </div>
      )}
    </div>
  );
}

function ChipButton({
  icon,
  count,
  dim,
  active,
  ariaLabel,
  onClick,
}: {
  icon: React.ReactNode;
  count: number;
  dim: boolean;
  active: boolean;
  ariaLabel: string;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-expanded={active}
      className={`inline-flex items-center justify-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-extrabold tracking-wide transition-colors ${
        active
          ? 'bg-navy-900 text-gold-500'
          : dim
          ? 'bg-navy-900/10 text-navy-900/50'
          : 'bg-navy-900/10 text-navy-900 hover:bg-navy-900/20'
      }`}
    >
      {icon}
      <span className="tabular-nums">×{count}</span>
    </button>
  );
}

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="0.5" strokeLinejoin="round" aria-hidden>
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    </svg>
  );
}

function SnowflakeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="m20 16-4-4 4-4" />
      <path d="m4 8 4 4-4 4" />
      <path d="m16 4-4 4-4-4" />
      <path d="m8 20 4-4 4 4" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
